import {
  createNewEquipment,
  getAllEquipments,
  getTotalActiveEquipments,
  getEquipmentById,
  listAllEquipments,
  updateEquipment,
  updateManyEquipments,
  listEquipmentsByGroupCheckpoint,
  getEquipmentByName,
  getMatchingEquipmentError,
  getEquipmentByClassId,
} from '@/api/equipment/equipment';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSortedList } from './list-order';
import { EquipmentDmgFilterType } from '@/entities/equipment';
import { getTranslatedItem } from '@/utils/language.utils';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateEquipment = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TEquipmentSchema) => createNewEquipment(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllEquipments'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveEquipments'] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingEquipmentError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllEquipments'] });

      notify(t('equipmentCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingEquipmentError = (query?: TEquipmentSchema) => {
  return useQuery({
    queryKey: ['getMatchingEquipmentError', query],
    queryFn: () => getMatchingEquipmentError(query),
  });
};

export const useListAllEquipments = () => {
  return useQuery({
    queryKey: ['listAllEquipments'],
    queryFn: () => listAllEquipments(),
  });
};

export const useGetModerationEquipments = (classIdAsValue = true) => {
  const { data: allEquipments, isLoading: isLoadingAllEquipments } =
    useListAllEquipments();

  let equipmentsSelectOptions = [];

  const options = getSortedList(allEquipments?.data, 'name', 'asc')?.map(
    (item: TEquipmentSchema) => {
      const translatedItem = getTranslatedItem(item?.['translatedEquipments']);

      return {
        label: translatedItem?.name || item?.name?.trim(),
        value: classIdAsValue ? `${item?.classId}` : item?.id,
        color: item?.color,
      };
    },
  );

  equipmentsSelectOptions = [...equipmentsSelectOptions, ...(options || [])];

  return {
    isLoading: isLoadingAllEquipments,
    equipmentsSelectOptions,
  };
};

export const useGetEquipmentByGroupCheckpoint = (
  groupId: string,
  checkPointId: string,
) => {
  return useQuery({
    queryKey: ['getEquipmentByGroupCheckpoint', groupId, checkPointId],
    queryFn: () => listEquipmentsByGroupCheckpoint(groupId, checkPointId),
  });
};

export const useGetAllEquipments = (
  filter: EquipmentDmgFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllEquipments', filter, page, sortBy, sortDirection],
    queryFn: () => getAllEquipments(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveEquipments = () => {
  return useQuery({
    queryKey: ['getTotalActiveEquipments'],
    queryFn: () => getTotalActiveEquipments(),
  });
};

export const useGetEquipmentById = (id: string) => {
  return useQuery({
    queryKey: ['getEquipmentById', id],
    queryFn: () => getEquipmentById(id),
    enabled: !!id,
  });
};

export const useGetEquipmentByClassId = (classId: number) => {
  return useQuery({
    queryKey: ['getEquipmentByClassId', classId],
    queryFn: () => getEquipmentByClassId(classId),
    enabled: !!classId,
  });
};

export const useGetEquipmentByName = (name: string) => {
  return useQuery({
    queryKey: ['getEquipmentByName', name],
    queryFn: () => getEquipmentByName(name),
    enabled: !!name,
  });
};

export const useGetEquipmentsSelectOptions = () => {
  const { data: allEquipments, isLoading: isLoadingAllEquipments } =
    useListAllEquipments();

  const getAllEquipmentsSelectOptions = () => {
    if (
      !isLoadingAllEquipments &&
      allEquipments &&
      allEquipments?.data?.length > 0
    ) {
      return allEquipments?.data?.map((equipment: TEquipmentSchema) => {
        return {
          label: equipment?.name,
          value: equipment?.id,
        };
      });
    }

    return undefined;
  };

  return {
    getAllEquipmentsSelectOptions,
    isLoadingAllEquipments,
  };
};

export const useUpdateEquipment = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (equipment: TEquipmentSchema) => updateEquipment(equipment),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllEquipments'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveEquipments'] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingEquipmentError'],
      });
      queryClient.invalidateQueries({ queryKey: ['getEquipmentById', id] });
      queryClient.invalidateQueries({ queryKey: ['listAllEquipments'] });

      notify(t('equipmentUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating equipment:', err.error);
    },
  });
};

export const useUpdateManyEquipments = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (equipments: TEquipmentSchema[]) =>
      updateManyEquipments(equipments),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getEquipmentById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllEquipments'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveEquipments'] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingEquipmentError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllEquipments'] });

      notify(t('equipmentUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating equipments:', error);
    },
  });
};
